export const locales = ["en", "zh"] as const;

export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = "zh";

export function isLocale(value: string): value is Locale {
  return locales.includes(value as Locale);
}

export const messages = {
  en: {
    heatmap: {
      title: "A-Share Market Heatmap",
      description:
        "A canvas-based market map that visualizes stocks by sector weight and performance color in real-time style.",
      backToHome: "Back to Home",
      marketLabel: "Market Universe",
      boardFilterLabel: "Primary Sector",
      allBoards: "All Sectors",
      selectedBoardCount: "{count} selected",
      clearBoardFilter: "Clear selection",
      trendFilterLabel: "Trend Filter",
      allTrends: "All",
      risingOnly: "Rising Only",
      fallingOnly: "Falling Only",
      changeRangeFilterLabel: "Change Range",
      changeRangeMinPlaceholder: "Min",
      changeRangeMaxPlaceholder: "Max",
      changeRangeUnbounded: "Any",
      clearChangeRangeFilter: "Clear",
      changeRangeEmpty: "No stocks match the current filters",
      watchlistManage: "Manage watchlist",
      watchlistAdd: "Add stocks",
      watchlistSearchPlaceholder: "Search by name or code",
      watchlistSearchEmpty: "No matching stocks",
      watchlistSearching: "Searching...",
      watchlistEmptyTitle: "Your watchlist is empty",
      watchlistEmptyHint: "Open Settings → Watchlist to add stocks by name or code.",
      watchlistAddedCount: "{count} added",
      watchlistRemove: "Remove",
      watchlistClear: "Clear all",
      watchlistClearConfirm: "Clear all {count} watchlist stocks? This cannot be undone.",
      watchlistClearConfirmAction: "Clear",
      watchlistClearCancel: "Cancel",
      watchlistAlreadyAdded: "Already in watchlist",
      watchlistAddSuccess: "Added {name}",
      watchlistRemoveSuccess: "Removed {name}",
      watchlistMaxReached: "Watchlist can hold up to {count} stocks",
      watchlistNotFound: "No matching stock found",
      watchlistClearSuccess: "Watchlist cleared",
      watchlistQuickAdd: "Add to Watchlist",
      watchlistQuickRemove: "Remove from Watchlist",
      inspectorWatchlistHint: "add / remove watchlist",
      watchlistExportAction: "Export",
      watchlistImportAction: "Import",
      watchlistImportSuccess: "Imported {added}, skipped {skipped}",
      watchlistImportFailed: "Invalid watchlist file",
      watchlistAiTitle: "AI screenshot recognition",
      watchlistAiOpen: "Add from screenshot",
      watchlistAiIntro:
        "Upload or paste a screenshot, then start recognition. Your API key stays in this browser and is sent only to the Base URL you set — never through this site’s servers.",
      watchlistAiCorsNote:
        "The provider must allow CORS from this site. If the browser blocks the request, use a CORS-enabled gateway or local proxy.",
      watchlistAiConfigure: "Configure AI",
      watchlistAiHideConfig: "Hide settings",
      watchlistAiBaseUrl: "Base URL",
      watchlistAiBaseUrlPlaceholder: "https://api.openai.com/v1",
      watchlistAiApiKey: "API Key",
      watchlistAiApiKeyPlaceholder: "sk-…",
      watchlistAiModel: "Model",
      watchlistAiModelPlaceholder: "gpt-4o-mini",
      watchlistAiShowKey: "Show",
      watchlistAiHideKey: "Hide",
      watchlistAiSave: "Save",
      watchlistAiSaved: "AI settings saved",
      watchlistAiNotConfigured: "Set Base URL, API key, and a vision-capable model first.",
      watchlistAiNeedConfigTitle: "Configure AI before recognizing screenshots",
      watchlistAiNeedConfigAction: "Open AI settings",
      watchlistAiRecognize: "Start recognition",
      watchlistAiRetry: "Retry recognition",
      watchlistAiUpload: "Choose image",
      watchlistAiPasteHint: "Or paste with Ctrl/⌘+V",
      watchlistAiDropHint: "Drop a screenshot here",
      watchlistAiReady: "Image ready — click to recognize",
      watchlistAiRecognizing: "Recognizing stocks…",
      watchlistAiPreparing: "Preparing image…",
      watchlistAiMatching: "Matching against stock universe…",
      watchlistAiNoStocks: "No A-share stocks were recognized",
      watchlistAiCandidatesTitle: "Recognition results",
      watchlistAiSelectAll: "Select all matched",
      watchlistAiClearSelection: "Clear selection",
      watchlistAiUnmatched: "No match in universe",
      watchlistAiHintLabel: "From screenshot",
      watchlistAiAddSelected: "Add selected ({count})",
      watchlistAiAddSelectedEmpty: "Select at least one matched stock",
      watchlistAiAddSuccess: "Added {count} stocks from screenshot",
      watchlistAiErrorCors:
        "Request blocked by CORS. The AI endpoint must allow browser calls from this site.",
      watchlistAiErrorConfig: "Check Base URL, API key, and model, then try again.",
      watchlistAiErrorNetwork: "Unable to reach the AI endpoint.",
      watchlistAiErrorParse: "The model response could not be parsed. Try another vision model.",
      watchlistAiErrorImage: "This image could not be processed. Try a PNG or JPEG screenshot.",
      watchlistAiErrorUnknown: "Recognition failed. Please try again.",
      watchlistAiDismiss: "Clear image",
      changeRangeGte: "≥{value}%",
      changeRangeLte: "≤{value}%",
      metricLabel: "Performance Window",
      resetView: "Reset View",
      enterFullscreen: "Fullscreen",
      exitFullscreen: "Exit Fullscreen",
      lastUpdated: "Last Updated",
      updating: "Updating…",
      loadingLiveData: "Loading live data…",
      zoomHint: "Use wheel to zoom and drag to pan.",
      hoverHint: "Hover any stock to see live details",
      legendRise: "Rising",
      legendFlat: "Flat",
      legendFall: "Falling",
      dataNote: "Data source: Eastmoney real-time snapshot (cached for a few seconds).",
      refreshData: "Refresh data",
      refreshDataHint: "The previous heatmap is still shown. Try refreshing again.",
      loading: "Loading heatmap...",
      loadingTipLabel: "Market tip",
      loadingTips: [
        "Larger blocks usually mean higher float market-cap weight in this view.",
        "Deeper red or green often means a bigger move. Zoom in to read stock names.",
        "Sector rotation tends to show up here before it hits the headlines.",
        "Fullscreen is handy when you want the whole market in one glance.",
        "Scroll to zoom and drag to pan, just like a map.",
        "Switch the market universe in the sidebar to narrow or widen the map.",
        "Build a personal watchlist heatmap by adding stocks by name or code.",
        "The bottom gradient maps color to performance.",
        "Hover a block to peek at price and change without leaving the page.",
      ],
      errorLoad: "Unable to load heatmap data at the moment.",
      periodDataUnavailable: "Performance data for this period is temporarily unavailable.",
      periodDataUnavailableHint: "Please retry later or choose another period.",
      canvasLabel: "A-share market heatmap canvas",
      priceLabel: "Price",
      changeLabel: "Change",
      marketCapLabel: "Market Cap",
      turnoverLabel: "Turnover",
      sizeModeLabel: "Block Size",
      sizeModeMarketCap: "Float Cap",
      sizeModeTurnover: "Turnover",
      thumbnailModeLabel: "Thumbnail",
      thumbnailModeOn: "On",
      thumbnailModeOff: "Off",
      headerTrendStatsLabel: "Sector title stats",
      headerTrendStatsOn: "On",
      headerTrendStatsOff: "Off",
      heatmapBordersLabel: "Heatmap borders",
      heatmapBordersOn: "Show",
      heatmapBordersOff: "Hide",
      heatmapBordersHint: "Hides solid outlines and retains spacing only between top-level sectors; the active stock uses a soft highlight.",
      settingsRefreshIntervalLabel: "Auto refresh",
      settingsRefreshIntervalUnit: "sec",
      settingsRefreshIntervalHint:
        "Quotes auto-refresh while the page is visible. Range {min}–{max} seconds; changes apply immediately and refetch once.",
      boardTrendCounts: "↑{advance} ↓{decline}",
      comparedToYesterdayLabel: "Vs Yesterday",
      turnoverIncreaseLabel: "Higher Volume",
      turnoverDecreaseLabel: "Lower Volume",
      turnoverFlatLabel: "Unchanged",
      boardCountLabel: "Sectors",
      fallbackDataLabel: "Sample snapshot",
      sampleDataLabel: "Sample data",
      operationTipsTitle: "Tips",
      tipAreaMarketCap: "· Area shows float market cap",
      tipAreaTurnover: "· Area shows live turnover",
      tipColor: "· Color shows performance change",
      tipThumbnail:
        "· Thumbnail view summarizes filtered sectors with change, advance/decline counts, and sized blocks",
      tipDoubleClick: "· Click a sector title to focus it; select multiple sectors in the sidebar; double-click a stock to open Xueqiu",
      tipTap: "· Tap a block to view stock details; tap a sector title to toggle it",
      tipZoom: "· Use the wheel to zoom into details",
      tipPinch: "· Pinch with two fingers to zoom",
      tipDrag: "· After zooming, hold and drag to pan the map",
      tipInspectorScroll: "· Hover a stock, then use ↑/↓ or J/K to scroll the popup list",
      tipInspectorSort: "· In the popup list, press S / Shift+S to change sorting",
      inspectorScrollHint: "↑/↓ or J/K scrolls the hover list",
      inspectorSortHint: "S / Shift+S changes sort",
      inspectorSortLabel: "Sort",
      inspectorSortChangeAbs: "Move",
      inspectorSortChangeDesc: "Gain",
      inspectorSortChangeAsc: "Loss",
      inspectorSortTurnover: "Turnover",
      inspectorSortName: "Name",
      boardBreadcrumbAll: "All",
      shareImage: "Capture & Share",
      generatingShareImage: "Generating...",
      sharePreviewTitle: "Share Preview",
      downloadImage: "Download",
      copyImage: "Copy",
      shareToApps: "Share",
      shareDialogTitle: "A-Share Market Heatmap",
      shareDialogText: "A-share market heatmap snapshot",
      shareSuccess: "Thanks for sharing!",
      shareCancelled: "Share cancelled",
      shareUnsupported: "Native share is not supported on this device",
      closePreview: "Close preview",
      copySuccess: "Image copied to clipboard",
      copyFailed: "Unable to copy image on this browser",
      shareFailed: "Unable to generate share image",
      fullscreenToast: "Press Esc to exit fullscreen.",
      fullscreenToastMobile: "Tap the icon in the top-right to exit fullscreen.",
      expandSidebar: "Open menu",
      collapseSidebar: "Close menu",
      openSettings: "Open settings",
      settingsTitle: "Settings",
      settingsDescription: "Tune the heatmap display and interaction preferences.",
      settingsAppearance: "Appearance",
      settingsWatchlist: "Watchlist",
      settingsWatchlistIntro: "Add stocks by name or code to build a personal heatmap.",
      settingsWebmcp: "WebMCP",
      webmcpTitle: "Use this page with AI",
      webmcpIntro:
        "This page exposes WebMCP tools so a compatible AI agent can read the heatmap, change the view, inspect rankings, and manage your local watchlist.",
      webmcpChatGPTTitle: "Test with ChatGPT desktop built-in browser",
      webmcpChatGPTStep1: "Open the latest ChatGPT desktop app and start a supported Work or Codex conversation.",
      webmcpChatGPTStep2: "Open the built-in browser, visit this page, and enable site tools if ChatGPT asks for permission.",
      webmcpChatGPTStep3: "When the address bar shows the site-tools indicator, ask ChatGPT to use the current page's Site tools.",
      webmcpPromptState:
        "Use the Site tools provided by the current page to read the heatmap state, then tell me the market, period, filters, and market summary. Do not use ordinary clicks.",
      webmcpPromptRanking:
        "Use the Site tools provided by the current page to switch to CSI 300, use the last 20 trading days, and list the 10 stocks with the largest gains.",
      webmcpPromptWatchlist:
        "Use the current page's Site tools to search for 宁德时代, add the exact stock to my watchlist, and then read the watchlist. Do not clear it.",
      webmcpPromptTheme:
        "Use the current page's Site tools to create and apply a cyberpunk heatmap theme: rising #00e5ff, falling #ff2bd6, flat #7c3aed.",
      webmcpCopyPrompt: "Copy prompt",
      webmcpPromptCopied: "Copied",
      webmcpPromptCopyFailed: "Unable to copy prompt",
      webmcpChromeTitle: "Chrome and Inspector testing",
      webmcpChromeDescription:
        "For development testing, enable Chrome's WebMCP flag and use the WebMCP Model Context Tool Inspector to inspect or call the registered tools. This is a debugging path, not ChatGPT's built-in Site tools flow.",
      webmcpInspectorLink: "Open WebMCP Model Context Tool Inspector",
      webmcpChromeDocsLink: "Read Chrome WebMCP documentation",
      webmcpBrowserTitle: "If no tool appears",
      webmcpBrowserDescription:
        "Site tools depend on your ChatGPT account, workspace, selected model, and the page being open in the built-in browser. A normal browser without WebMCP support continues to work, but it cannot perform a real AI tool call.",
      settingsLanguage: "Language",
      settingsShortcuts: "Shortcuts",
      settingsShortcutsIntro: "Click a row, then press a new key. Esc cancels. Escape itself cannot be remapped.",
      settingsShortcutsRecording: "Press a new key…",
      settingsShortcutsConflict: "Already used by “{action}”. Pick another key.",
      settingsShortcutsInvalid: "Use a single letter, number, or comma.",
      settingsShortcutsReset: "Restore defaults",
      shortcutActionShare: "Capture & Share",
      shortcutActionResetView: "Reset View",
      shortcutActionFullscreen: "Fullscreen",
      shortcutActionSettings: "Settings",
      shortcutActionSidebar: "Toggle Sidebar",
      shortcutActionDisplayMode: "Light / Dark",
      shortcutActionFilters: "Filters",
      shortcutActionToggleWatchlist: "Add / Remove Watchlist",
      filtersTitle: "Filters",
      filtersDescription: "Narrow the map by sector, trend, change range, period, and block size.",
      filtersOpen: "Open filters",
      filtersBack: "Back",
      filtersReset: "Reset filters",
      filtersIdleHint: "No extra filters",
      helpShortcutsTitle: "Keyboard shortcuts",
      helpShortcutsCta: "Open shortcut settings",
      settingsHelp: "Help",
      settingsProject: "Project",
      displayMode: "Display mode",
      lightMode: "Light",
      darkMode: "Dark",
      filterOpenModeLabel: "Filter panel trigger",
      filterOpenModeClick: "Click (Recommended)",
      filterOpenModeHover: "Hover",
      themeColor: "Accent color",
      priceColor: "Price polarity",
      heatThemeLabel: "Heatmap palette",
      heatThemeSoft: "Soft",
      heatThemeClassic: "Classic",
      heatThemeMuted: "Muted",
      heatThemeHighContrast: "High Contrast",
      heatThemeCustom: "Custom",
      heatThemeCreateCustom: "Customize from current",
      heatThemeEdit: "Edit",
      heatThemeSave: "Save",
      heatThemeCancel: "Cancel",
      heatThemeSaved: "Theme saved",
      heatThemeEditingHint: "Editing draft — save to apply, or cancel to discard.",
      heatThemeDeleteCustom: "Delete",
      heatThemeExport: "Export theme",
      heatThemeImport: "Import theme",
      heatThemeImportSuccess: "Theme imported",
      heatThemeImportFailed: "Unable to import this theme file",
      heatThemeExportFailed: "Unable to export theme",
      heatThemeNameZh: "Chinese name",
      heatThemeNameEn: "English name",
      heatThemeEditDark: "Dark mode colors",
      heatThemeEditLight: "Light mode colors",
      heatStopFlat: "Flat",
      heatStopPositiveSoft: "Rise · soft",
      heatStopPositiveStrong: "Rise · strong",
      heatStopNegativeSoft: "Fall · soft",
      heatStopNegativeStrong: "Fall · strong",
      redRiseGreenFall: "Red rises, green falls",
      greenRiseRedFall: "Green rises, red falls",
      languageLabel: "Language",
      languageZh: "中文",
      languageEn: "English",
      helpTitle: "How to use",
      helpIntro: "Read the market by area, color, and sector grouping.",
      githubProject: "GitHub project",
      githubProjectDescription: "View source code, report issues, or star the repository.",
      projectAuthorTitle: "Author",
      projectAuthorDescription: "Bug reports, ideas, collaboration — or just saying hi.",
      projectAuthorEmail: "hi@wenyuanw.me",
      projectAdTitle: "Ad slot for rent",
      projectAdDescription:
        "This corner is currently empty, staring into the void. If you have something cool to show A-share map watchers, drop an email — maybe your logo lands here.",
      projectAdCta: "Inquire about this spot",
      mobileTapHint: "Tap any block to see stock details.",
      mobileOpenInXueqiu: "Open on Xueqiu",
      closeSheet: "Close",
      markets: {
        all: "A-Share Full Market",
        sse: "Shanghai A-Share",
        szse: "Shenzhen A-Share",
        hs300: "CSI 300",
        zza50: "CSI A50",
        zza500: "CSI A500",
        main: "A-Share Main Board",
        cyb: "ChiNext",
        kcb: "STAR Market",
        watchlist: "Watchlist",
      },
      metrics: {
        day: "Today",
        previousDay: "Yesterday",
        week: "Last 5 Trading Days",
        month: "Last 20 Trading Days",
        yearToDate: "Year to Date",
        year: "Year to Date",
      },
    },
  },
  zh: {
    heatmap: {
      title: "A 股市场热力图",
      description:
        "基于 Canvas 的市场云图，按行业权重展示股票面积，用颜色呈现涨跌表现。",
      backToHome: "返回首页",
      marketLabel: "市场范围",
      boardFilterLabel: "一级板块",
      allBoards: "全部板块",
      selectedBoardCount: "已选 {count}",
      clearBoardFilter: "取消选择",
      trendFilterLabel: "涨跌筛选",
      allTrends: "全部",
      risingOnly: "上涨",
      fallingOnly: "下跌",
      changeRangeFilterLabel: "涨跌幅范围",
      changeRangeMinPlaceholder: "最小",
      changeRangeMaxPlaceholder: "最大",
      changeRangeUnbounded: "不限",
      clearChangeRangeFilter: "清除",
      changeRangeEmpty: "当前筛选无匹配个股",
      watchlistManage: "管理自选",
      watchlistAdd: "添加股票",
      watchlistSearchPlaceholder: "输入名称或代码",
      watchlistSearchEmpty: "未找到匹配股票",
      watchlistSearching: "搜索中...",
      watchlistEmptyTitle: "还没有自选股票",
      watchlistEmptyHint: "打开设置中的「自选」，通过名称或代码添加股票。",
      watchlistAddedCount: "已添加 {count}",
      watchlistRemove: "移除",
      watchlistClear: "清空",
      watchlistClearConfirm: "确认清空全部 {count} 只自选？此操作无法撤销。",
      watchlistClearConfirmAction: "确认清空",
      watchlistClearCancel: "取消",
      watchlistAlreadyAdded: "已在自选中",
      watchlistAddSuccess: "已添加 {name}",
      watchlistRemoveSuccess: "已移除 {name}",
      watchlistMaxReached: "最多添加 {count} 只股票",
      watchlistNotFound: "未找到匹配的股票",
      watchlistClearSuccess: "已清空自选",
      watchlistQuickAdd: "加入自选",
      watchlistQuickRemove: "移出自选",
      inspectorWatchlistHint: "加入/移除自选",
      watchlistExportAction: "导出",
      watchlistImportAction: "导入",
      watchlistImportSuccess: "已导入 {added} 条，跳过 {skipped} 条",
      watchlistImportFailed: "自选股文件格式无效",
      watchlistAiTitle: "AI 截图识股",
      watchlistAiOpen: "从截图添加",
      watchlistAiIntro:
        "上传或粘贴截图后，再点击开始识别。API Key 只保存在本机浏览器，请求直连你填写的 Base URL，不会经过本站服务器。",
      watchlistAiCorsNote:
        "接口需允许本站跨域（CORS）。若浏览器拦截请求，请改用已开启 CORS 的网关或本地代理。",
      watchlistAiConfigure: "配置 AI",
      watchlistAiHideConfig: "收起配置",
      watchlistAiBaseUrl: "Base URL",
      watchlistAiBaseUrlPlaceholder: "https://api.openai.com/v1",
      watchlistAiApiKey: "API Key",
      watchlistAiApiKeyPlaceholder: "sk-…",
      watchlistAiModel: "模型",
      watchlistAiModelPlaceholder: "gpt-4o-mini",
      watchlistAiShowKey: "显示",
      watchlistAiHideKey: "隐藏",
      watchlistAiSave: "保存",
      watchlistAiSaved: "AI 配置已保存",
      watchlistAiNotConfigured: "请先填写 Base URL、API Key 和支持识图的模型。",
      watchlistAiNeedConfigTitle: "请先配置 AI，再识别截图",
      watchlistAiNeedConfigAction: "去配置",
      watchlistAiRecognize: "开始识别",
      watchlistAiRetry: "重新识别",
      watchlistAiUpload: "选择图片",
      watchlistAiPasteHint: "也可按 Ctrl/⌘+V 粘贴",
      watchlistAiDropHint: "将截图拖到此处",
      watchlistAiReady: "图片已就绪，点击开始识别",
      watchlistAiRecognizing: "正在识别股票…",
      watchlistAiPreparing: "正在处理图片…",
      watchlistAiMatching: "正在匹配成分库…",
      watchlistAiNoStocks: "未识别到 A 股个股",
      watchlistAiCandidatesTitle: "识别结果",
      watchlistAiSelectAll: "全选已匹配",
      watchlistAiClearSelection: "取消选择",
      watchlistAiUnmatched: "成分库无匹配",
      watchlistAiHintLabel: "截图识别",
      watchlistAiAddSelected: "加入已选 ({count})",
      watchlistAiAddSelectedEmpty: "请至少选择一只已匹配股票",
      watchlistAiAddSuccess: "已从截图添加 {count} 只股票",
      watchlistAiErrorCors: "请求被 CORS 拦截。请确认 AI 接口允许本站浏览器直连。",
      watchlistAiErrorConfig: "请检查 Base URL、API Key 和模型后重试。",
      watchlistAiErrorNetwork: "无法连接 AI 接口。",
      watchlistAiErrorParse: "无法解析模型返回，可换一个支持识图的模型再试。",
      watchlistAiErrorImage: "图片无法处理，请改用 PNG 或 JPEG 截图。",
      watchlistAiErrorUnknown: "识别失败，请稍后重试。",
      watchlistAiDismiss: "清除图片",
      changeRangeGte: "≥{value}%",
      changeRangeLte: "≤{value}%",
      metricLabel: "涨跌区间",
      resetView: "重置视图",
      enterFullscreen: "全屏",
      exitFullscreen: "退出全屏",
      lastUpdated: "最近刷新",
      updating: "更新中…",
      loadingLiveData: "正在加载实时数据…",
      zoomHint: "滚轮缩放，按住拖拽平移。",
      hoverHint: "将鼠标移动到任意股票上查看详情",
      legendRise: "上涨",
      legendFlat: "平盘",
      legendFall: "下跌",
      dataNote: "数据来源：东方财富实时快照（秒级缓存）。",
      refreshData: "刷新数据",
      refreshDataHint: "当前仍展示上一次热力图数据，可点击刷新重试。",
      loading: "热力图加载中...",
      loadingTipLabel: "市场小贴士",
      loadingTips: [
        "色块越大，通常表示在该视图里流通市值权重越高。",
        "红绿色越深，往往波动越显著，滚轮放大即可看清个股名称。",
        "板块轮动在这张热力图上，常常比新闻更快显形。",
        "想一眼扫全市场时，可以试试全屏，视野更完整。",
        "滚轮缩放、拖拽平移，像在地图里逛行情。",
        "左侧切换市场范围，可以把地图缩小到某只指数或板块。",
        "在侧栏打开自选，用名称或代码添加股票，就能生成个人热力图。",
        "底部渐变色条是颜色与涨跌幅的对照尺，方便对齐直觉。",
        "悬停色块就能看价格与涨跌幅，不必跳转新页面。",
      ],
      errorLoad: "当前无法加载热力图数据，请稍后重试。",
      periodDataUnavailable: "该周期涨跌数据暂不可用。",
      periodDataUnavailableHint: "请稍后重试，或选择其他周期。",
      canvasLabel: "A 股市场热力图画布",
      priceLabel: "价格",
      changeLabel: "涨跌幅",
      marketCapLabel: "市值",
      turnoverLabel: "成交额",
      sizeModeLabel: "面积指标",
      sizeModeMarketCap: "流通市值",
      sizeModeTurnover: "成交额",
      thumbnailModeLabel: "缩略图",
      thumbnailModeOn: "开",
      thumbnailModeOff: "关",
      headerTrendStatsLabel: "板块标题栏统计",
      headerTrendStatsOn: "开",
      headerTrendStatsOff: "关",
      heatmapBordersLabel: "热力图边框",
      heatmapBordersOn: "显示",
      heatmapBordersOff: "隐藏",
      heatmapBordersHint: "隐藏实线边框，仅保留一级板块间隙；当前股票会以柔和提亮进行标识。",
      settingsRefreshIntervalLabel: "自动刷新",
      settingsRefreshIntervalUnit: "秒",
      settingsRefreshIntervalHint:
        "页面可见时自动刷新行情，范围 {min}–{max} 秒；修改后立即生效并马上刷新一次。",
      boardTrendCounts: "涨{advance} 跌{decline}",
      comparedToYesterdayLabel: "比昨日",
      turnoverIncreaseLabel: "放量",
      turnoverDecreaseLabel: "缩量",
      turnoverFlatLabel: "持平",
      boardCountLabel: "板块数",
      fallbackDataLabel: "当前为样本快照",
      sampleDataLabel: "样本数据",
      operationTipsTitle: "操作提示",
      tipAreaMarketCap: "· 面积代表流通市值",
      tipAreaTurnover: "· 面积代表实时成交额",
      tipColor: "· 颜色代表涨跌幅度",
      tipThumbnail: "· 开启缩略图可查看已筛选的一、二级板块统计，面积对应市值或成交额",
      tipDoubleClick: "· 单击一级板块标题可聚焦；侧栏可同时选择多个板块；双击个股前往雪球",
      tipTap: "· 点击色块查看个股详情；点击板块标题可筛选板块",
      tipZoom: "· 滚轮缩放查看细节",
      tipPinch: "· 双指捏合缩放查看细节",
      tipDrag: "· 缩放后按住鼠标可拖动画布",
      tipInspectorScroll: "· 悬浮个股后，可用 ↑/↓ 或 J/K 滚动弹窗列表",
      tipInspectorSort: "· 弹窗列表中按 S / Shift+S 切换排序",
      inspectorScrollHint: "↑/↓ 或 J/K 可滚动悬浮列表",
      inspectorSortHint: "S / Shift+S 切换排序",
      inspectorSortLabel: "排序",
      inspectorSortChangeAbs: "振幅",
      inspectorSortChangeDesc: "涨幅",
      inspectorSortChangeAsc: "跌幅",
      inspectorSortTurnover: "成交额",
      inspectorSortName: "名称",
      boardBreadcrumbAll: "全部",
      shareImage: "截图分享",
      generatingShareImage: "生成中...",
      sharePreviewTitle: "截图预览",
      downloadImage: "下载图片",
      copyImage: "复制图片",
      shareToApps: "分享",
      shareDialogTitle: "A 股市场热力图",
      shareDialogText: "A 股市场热力图快照",
      shareSuccess: "已发起分享",
      shareCancelled: "已取消分享",
      shareUnsupported: "当前设备不支持系统分享",
      closePreview: "关闭预览",
      copySuccess: "图片已复制到剪贴板",
      copyFailed: "当前浏览器无法直接复制图片",
      shareFailed: "当前无法生成截图，请稍后重试",
      fullscreenToast: "按 Esc 退出全屏",
      fullscreenToastMobile: "点击右上角按钮退出全屏",
      expandSidebar: "展开菜单",
      collapseSidebar: "收起菜单",
      openSettings: "打开设置",
      settingsTitle: "设置",
      settingsDescription: "调整热力图的显示、语言和看盘偏好。",
      settingsAppearance: "外观",
      settingsWatchlist: "自选",
      settingsWatchlistIntro: "通过股票名称或代码添加，生成你的专属热力图。",
      settingsWebmcp: "WebMCP",
      webmcpTitle: "让 AI 操作当前页面",
      webmcpIntro:
        "本页面提供了一组 WebMCP 工具，兼容的 AI Agent 可以读取热力图、切换视图、查询排行，并管理保存在本机浏览器中的自选股。",
      webmcpChatGPTTitle: "使用 ChatGPT 桌面版内置浏览器测试",
      webmcpChatGPTStep1: "打开最新版 ChatGPT 桌面版，并创建支持 Site tools 的 Work 或 Codex 对话。",
      webmcpChatGPTStep2: "打开 ChatGPT 内置浏览器，访问当前页面；如果出现权限提示，允许使用 Site tools。",
      webmcpChatGPTStep3: "地址栏出现 Site tools 指示图标后，在同一个对话中要求 ChatGPT 使用当前页面提供的 Site tools。",
      webmcpPromptState:
        "请使用当前页面提供的 Site tools 读取热力图状态，然后告诉我当前市场范围、周期、筛选条件和市场概览。不要使用普通点击操作。",
      webmcpPromptRanking:
        "请使用当前页面提供的 Site tools 切换到沪深 300，设置为近 20 日，并列出涨幅最大的 10 只股票。",
      webmcpPromptWatchlist:
        "请使用当前页面的 Site tools 搜索宁德时代，把准确的股票加入我的自选股，然后读取自选股。不要清空自选股。",
      webmcpPromptTheme:
        "请使用当前页面的 Site tools 创建并应用一个赛博朋克风格的热力图主题：上涨 #00e5ff，下跌 #ff2bd6，横盘 #7c3aed。",
      webmcpCopyPrompt: "复制提示词",
      webmcpPromptCopied: "已复制",
      webmcpPromptCopyFailed: "提示词复制失败",
      webmcpChromeTitle: "使用 Chrome 和 Inspector 调试",
      webmcpChromeDescription:
        "开发测试时，可以开启 Chrome 的 WebMCP flag，再使用 WebMCP Model Context Tool Inspector 查看或调用已注册工具。这是调试路径，不是 ChatGPT 内置 Site tools 的使用流程。",
      webmcpInspectorLink: "打开 WebMCP Model Context Tool Inspector",
      webmcpChromeDocsLink: "查看 Chrome WebMCP 官方文档",
      webmcpBrowserTitle: "如果看不到工具",
      webmcpBrowserDescription:
        "Site tools 是否出现取决于 ChatGPT 账号、工作区、当前模型，以及页面是否在 ChatGPT 内置浏览器中打开。不支持 WebMCP 的普通浏览器仍可正常使用网页，但不能完成真实的 AI 工具调用。",
      settingsLanguage: "语言",
      settingsShortcuts: "快捷键",
      settingsShortcutsIntro: "点击一项后按下新按键即可绑定。Esc 取消录制；Esc 本身不可重映射。",
      settingsShortcutsRecording: "请按下新按键…",
      settingsShortcutsConflict: "与「{action}」冲突，请换一个键。",
      settingsShortcutsInvalid: "请使用单个字母、数字或逗号。",
      settingsShortcutsReset: "恢复默认",
      shortcutActionShare: "截图分享",
      shortcutActionResetView: "重置视图",
      shortcutActionFullscreen: "全屏",
      shortcutActionSettings: "设置",
      shortcutActionSidebar: "展开/收起侧栏",
      shortcutActionDisplayMode: "浅色/深色",
      shortcutActionFilters: "筛选",
      shortcutActionToggleWatchlist: "加入/移除自选",
      filtersTitle: "筛选",
      filtersDescription: "按板块、涨跌方向、涨跌幅范围、周期和面积指标缩小当前视图。",
      filtersOpen: "打开筛选",
      filtersBack: "返回",
      filtersReset: "重置筛选",
      filtersIdleHint: "未额外筛选",
      helpShortcutsTitle: "键盘快捷键",
      helpShortcutsCta: "前往快捷键设置",
      settingsHelp: "帮助",
      settingsProject: "项目",
      displayMode: "显示模式",
      lightMode: "明亮模式",
      darkMode: "暗黑模式",
      filterOpenModeLabel: "筛选面板打开方式",
      filterOpenModeClick: "点击（推荐）",
      filterOpenModeHover: "悬停",
      themeColor: "强调色",
      priceColor: "涨跌极性",
      heatThemeLabel: "热力图配色",
      heatThemeSoft: "柔和",
      heatThemeClassic: "经典",
      heatThemeMuted: "低饱和",
      heatThemeHighContrast: "高对比",
      heatThemeCustom: "自定义",
      heatThemeCreateCustom: "基于当前自定义",
      heatThemeEdit: "编辑",
      heatThemeSave: "保存",
      heatThemeCancel: "取消",
      heatThemeSaved: "主题已保存",
      heatThemeEditingHint: "正在编辑草稿，保存后才会生效；取消则丢弃修改。",
      heatThemeDeleteCustom: "删除",
      heatThemeExport: "导出主题",
      heatThemeImport: "导入主题",
      heatThemeImportSuccess: "主题已导入",
      heatThemeImportFailed: "无法导入该主题文件",
      heatThemeExportFailed: "无法导出主题",
      heatThemeNameZh: "中文名称",
      heatThemeNameEn: "英文名称",
      heatThemeEditDark: "暗色模式颜色",
      heatThemeEditLight: "亮色模式颜色",
      heatStopFlat: "平盘",
      heatStopPositiveSoft: "涨 · 弱",
      heatStopPositiveStrong: "涨 · 强",
      heatStopNegativeSoft: "跌 · 弱",
      heatStopNegativeStrong: "跌 · 强",
      redRiseGreenFall: "红涨绿跌",
      greenRiseRedFall: "绿涨红跌",
      languageLabel: "语言",
      languageZh: "中文",
      languageEn: "English",
      helpTitle: "使用帮助",
      helpIntro: "通过面积、颜色和行业分组快速阅读市场。",
      githubProject: "GitHub 项目",
      githubProjectDescription: "查看源码、提交 Issue 或收藏仓库。",
      projectAuthorTitle: "作者",
      projectAuthorDescription: "提 bug、聊想法、谈合作，或者单纯打个招呼都行。",
      projectAuthorEmail: "hi@wenyuanw.me",
      projectAdTitle: "广告位招租",
      projectAdDescription:
        "这里暂时空着，有点寂寞。如果你有想给看盘人看见的东西，发封邮件过来——说不定下次刷新，你的 Logo 就坐在这儿了。",
      projectAdCta: "来问问这个位子",
      mobileTapHint: "点击任意色块查看个股详情",
      mobileOpenInXueqiu: "雪球查看",
      closeSheet: "关闭",
      markets: {
        all: "A 股全图",
        sse: "上证 A 股",
        szse: "深证 A 股",
        hs300: "沪深 300",
        zza50: "中证 A50",
        zza500: "中证 A500",
        main: "A 股主板",
        cyb: "创业板",
        kcb: "科创板",
        watchlist: "自选",
      },
      metrics: {
        day: "当日涨跌",
        previousDay: "昨日涨跌",
        week: "近 5 日涨跌",
        month: "近 20 日涨跌",
        yearToDate: "今年以来",
        year: "今年以来",
      },
    },
  },
} as const;

export function getMessages(locale: Locale) {
  return messages[locale];
}

export type Messages = (typeof messages)[Locale];
export type HeatmapMessages = Messages["heatmap"];
